// Sign-in, whichever provider fronts the Ledger. The API checks the token;
// the web app only needs to get one, keep it fresh and ask again when it ends.

export interface AuthUser {
  id: string;
  email: string | null;
  name: string | null;
}

export type SignInResult = { status: 'signed_in' } | { status: 'mfa_required' };

export interface AuthClient {
  /** 'password' providers show email and password; 'redirect' ones send people to the provider's own page. */
  readonly style: 'password' | 'redirect';
  /** For redirect providers: the button text, such as "Sign in with Microsoft". */
  readonly label?: string;
  current(): Promise<AuthUser | null>;
  /** A current access token, refreshed if it is about to expire; null when signed out. */
  accessToken(): Promise<string | null>;
  signInWithPassword?(email: string, password: string): Promise<SignInResult>;
  /** Second step for accounts with an authenticator app (TOTP), as CertTrack uses. */
  verifyCode?(code: string): Promise<void>;
  sendSignInLink?(email: string, returnTo: string): Promise<void>;
  sendPasswordReset?(email: string): Promise<void>;
  signInWithRedirect?(returnTo: string): Promise<void>;
  /** On /auth/callback: finish a redirect or emailed-link sign-in; returns where to go next. */
  completeSignIn(): Promise<string>;
  signOut(): Promise<void>;
  /** Called when the provider signs the person in or out (another tab, a refresh that failed). */
  onChange(listener: (user: AuthUser | null) => void): () => void;
}

/** Where to send someone after sign-in: only paths inside this app. */
export function safeReturnTo(value: string | null | undefined): string {
  return value && value.startsWith('/') && !value.startsWith('//') && !value.startsWith('/auth/') ? value : '/';
}

/** Turn provider messages into plain words. */
export function friendlyAuthError(e: unknown): string {
  const m = (e instanceof Error ? e.message : String(e)).toLowerCase();
  if (m.includes('invalid login') || m.includes('invalid credentials') || m.includes('invalid_grant')) return 'That email and password don’t match. Check them, or use “Email me a sign-in link”.';
  if (m.includes('email not confirmed')) return 'Confirm your email address first: open the link in the email we sent you.';
  if (m.includes('invalid totp') || m.includes('invalid code') || m.includes('mfa')) return 'That code isn’t right, or it has expired. Enter the current code from your authenticator app.';
  if (m.includes('rate limit') || m.includes('too many')) return 'Too many attempts. Wait a minute, then try again.';
  if (m.includes('fetch') || m.includes('network')) return 'Can’t reach the sign-in service. Check your internet connection and try again.';
  return e instanceof Error ? e.message : String(e);
}
